import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ChevronUp, Check } from 'lucide-react';

/**
 * Floating detail card that appears when a hero satellite is clicked.
 *
 * Desktop: overlays the hero's left column with a glass card; entrance slides
 *          in from the left.
 * Mobile : bottom sheet — full-width, anchored to the bottom of the viewport,
 *          slides up.
 *
 * Closes on:
 *   - click on the X button
 *   - click outside the panel (excluding clicks on the orbit cards so users
 *     can swap between services without the panel closing first)
 *   - ESC key
 *
 * Content crossfades when the user clicks a different satellite while the
 * panel is already open (handled by AnimatePresence + a per-service key).
 */
const OrbitDetailPanel = ({ services, activeIndex, onClose }) => {
    const isOpen = activeIndex !== null && activeIndex !== undefined;
    const service = isOpen ? services[activeIndex] : null;
    const panelRef = useRef(null);
    const [expanded, setExpanded] = useState(false);

    // Collapse the "Learn more" detail whenever the user opens a new service
    // (or closes the panel). Each service should start collapsed.
    useEffect(() => {
        setExpanded(false);
    }, [activeIndex]);

    // ESC to close.
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    // Click-outside-to-close. We listen at document level instead of using a
    // full-screen backdrop element so clicks on the orbit cards still reach
    // them (and trigger a content swap rather than closing the panel).
    useEffect(() => {
        if (!isOpen) return undefined;
        const onPointerDown = (e) => {
            const panel = panelRef.current;
            if (!panel) return;
            if (panel.contains(e.target)) return;
            // Don't close when the user clicks a satellite card — that should
            // just swap the panel content via the card's own onClick.
            if (e.target.closest('[data-testid^="hero-orbit-card-"]')) return;
            onClose();
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown, { passive: true });
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
        };
    }, [isOpen, onClose]);

    const handleLearnMoreToggle = (e) => {
        e.preventDefault();
        setExpanded((v) => !v);
    };

    const handleGetQuote = (e) => {
        e.preventDefault();
        // Send the user to the contact form pre-filled with this service slug.
        // Contact.jsx reads ?service=<slug> on mount.
        const slug = service?.slug ? `?service=${encodeURIComponent(service.slug)}` : '';
        window.location.assign(`/contact${slug}`);
    };

    return (
        <AnimatePresence>
            {isOpen && service && (
                <motion.div
                    key="orbit-detail-shell"
                    className="orbit-detail-shell"
                    data-testid="orbit-detail-shell"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                    <aside
                        ref={panelRef}
                        role="dialog"
                        aria-modal="false"
                        aria-labelledby="orbit-detail-title"
                        className="orbit-detail-panel"
                        data-testid="orbit-detail-panel"
                    >
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close service details"
                            data-testid="orbit-detail-close"
                            className="orbit-detail-close"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        {/* Crossfade content when activeIndex changes. */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={service.title}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -6 }}
                                transition={{ duration: 0.22, ease: 'easeOut' }}
                                data-testid="orbit-detail-content"
                            >
                                <div className="orbit-detail-head">
                                    <span className="orbit-detail-icon">
                                        <service.icon className="w-[26px] h-[26px]" strokeWidth={1.8} />
                                    </span>
                                    <h3 id="orbit-detail-title" className="orbit-detail-title">
                                        {service.title}
                                    </h3>
                                </div>

                                <p className="orbit-detail-desc">{service.description}</p>

                                <ul className="orbit-detail-features" data-testid="orbit-detail-features">
                                    {service.features.map((feat) => (
                                        <li key={feat} className="orbit-detail-feature">
                                            <span className="orbit-detail-feature-dot" />
                                            {feat}
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    type="button"
                                    onClick={handleLearnMoreToggle}
                                    className="orbit-detail-cta"
                                    data-testid="orbit-detail-cta"
                                    aria-expanded={expanded}
                                >
                                    {expanded ? (
                                        <>Show less <ChevronUp className="w-4 h-4" /></>
                                    ) : (
                                        <>Learn more <ArrowRight className="w-4 h-4" /></>
                                    )}
                                </button>

                                {/* Expanded "What you get" detail panel — smoothly grows in height. */}
                                <AnimatePresence initial={false}>
                                    {expanded && (
                                        <motion.div
                                            key="orbit-detail-expanded"
                                            initial={{ maxHeight: 0, opacity: 0, marginTop: 0 }}
                                            animate={{ maxHeight: 640, opacity: 1, marginTop: 18 }}
                                            exit={{ maxHeight: 0, opacity: 0, marginTop: 0 }}
                                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                            className="orbit-detail-expanded"
                                            data-testid="orbit-detail-expanded"
                                            style={{ overflow: 'hidden' }}
                                        >
                                            <div className="orbit-detail-expanded-inner">
                                                <div className="orbit-detail-expanded-label">What you get</div>
                                                <ul
                                                    className="orbit-detail-checklist"
                                                    data-testid="orbit-detail-checklist"
                                                >
                                                    {(service.whatYouGet || []).map((item) => (
                                                        <li key={item} className="orbit-detail-checklist-item">
                                                            <span className="orbit-detail-check">
                                                                <Check className="w-3 h-3" strokeWidth={3} />
                                                            </span>
                                                            <span>{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>

                                                {service.priceFrom && (
                                                    <span
                                                        className="orbit-detail-price"
                                                        data-testid="orbit-detail-price"
                                                    >
                                                        Starting from {service.priceFrom}
                                                    </span>
                                                )}

                                                <button
                                                    type="button"
                                                    onClick={handleGetQuote}
                                                    className="orbit-detail-quote"
                                                    data-testid="orbit-detail-quote"
                                                >
                                                    Get a quote <ArrowRight className="w-4 h-4" />
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={handleLearnMoreToggle}
                                                    className="orbit-detail-show-less"
                                                    data-testid="orbit-detail-show-less"
                                                >
                                                    <ChevronUp className="w-3.5 h-3.5" /> Show less
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        </AnimatePresence>
                    </aside>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default OrbitDetailPanel;
